import { memo, useCallback, useMemo } from 'react';
import { useDeleteLineIntegration } from '../../../api/delete-integration-line.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_LINE_QUERY_KEY } from '../../../api/get-list-integration-line.api';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteIntegrationLineModalType = 'DeleteIntegrationLineModal';
export interface IDeleteIntegrationLineModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_INTEGRATION_LINE = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete line integration!',
};

const DeleteIntegrationLineModal = memo(({ organizationChannelId }: IDeleteIntegrationLineModalProps) => {
  const channelModalRootActions = useChannelModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteIntegrationLineModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteIntegrationLineModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationLine = useDeleteLineIntegration({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_INTEGRATION_LINE.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_LINE_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteIntegrationLineModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_INTEGRATION_LINE.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteIntegrationLine.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }
  }, [mutationDeleteIntegrationLine, organizationChannelId]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteIntegrationLineModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteIntegrationLineModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationLine.isPending}
      title="Delete Integration Line"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteIntegrationLineModal.displayName = 'DeleteIntegrationLineModal';

export default DeleteIntegrationLineModal;
